// Copyright (c) 2020 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import { put } from "redux-saga/effects";
import { Store, Unsubscribe } from "@reduxjs/toolkit";

import { registerDataListener as registerDataListenerGql } from "graphql/mutations";
import {
  onUpdateLiveData as onUpdateLiveDataGql,
  onNewTrendData as onNewTrendDataGql,
  onNewAlarmData as onNewAlarmDataGql,
  onNewReport as onNewReportGql,
  onNewChargerData as onNewChargerDataGql,
  onNewChargingSessionData as onNewChargingSessionGql,
  onNewChargerStatusNotification,
} from "graphql/subscriptions";
import {
  OnUpdateLiveDataSubscription,
  OnNewTrendDataSubscription,
  OnNewTrendDataSubscriptionVariables,
  OnUpdateLiveDataSubscriptionVariables,
  OnNewAlarmDataSubscriptionVariables,
  OnNewAlarmDataSubscription,
  OnNewReportSubscription,
  OnNewReportSubscriptionVariables,
  RegisterDataListenerInput,
  Command,
  ReportFormat,
  OnNewChargerDataSubscriptionVariables,
  OnNewChargerDataSubscription,
  OnNewChargingSessionDataSubscriptionVariables,
  OnNewChargingSessionDataSubscription,
  OnNewChargerStatusNotificationSubscriptionVariables,
  OnNewChargerStatusNotificationSubscription,
  RegisterDataListenerMutation,
} from "graphql/api";
import { V6Client } from "@aws-amplify/api-graphql";
import { GraphQLResult } from "types";

import {
  ActionType,
  raiseAction,
  IActionNewReport,
  IActionNewReportData,
  IActionNewChargerData,
  IActionNewChargingSessionData,
  IActionNewChargerStatusNotification,
} from "actions";
import { IVirtAssign, IVirtSiteAssign } from "shared/types/cloudtypes";
import { isSet } from "shared/utils/utils";
import { parseApiInt } from "shared/utils/apiutils";
import { ConnectorStatus, StopReason } from "model/connector";
import { AlarmSource, IAlarmEntry } from "shared/types/globtypes";
import { IState } from "states/states";
import { getAlarmState, ILiveDataCloud } from "./apisagas";
import { cloudTrend2LinxTrend } from "./cloudsagas";
import { IOcppChargingSession } from "states/ocppstates";

export interface IRegisterPushSelection {
  needsLiveData: boolean;
  needsReportData: boolean;
  needsAlarmData: boolean;
  needsChargerData: boolean;
  needsControllerChargerData: boolean;
}

interface ISubscription {
  unsubscribe: Unsubscribe;
}

export class CloudLiveDataHandler {
  protected readonly CLOUD_COMMAND_TIMEOUT = 10000; // ms, timeout for command confirmation message

  private API;
  private store: Store | undefined;
  private clientToken: string | undefined; // the token to identify this client at LINX site
  private registeredSiteId: number | undefined; // site we registered live data for

  protected graphQlSubscription: {
    liveData: ISubscription | undefined; // subscribe only for main site
    newPeriod: ISubscription | undefined; // subscribe only for main site
    newAlarm: ISubscription | undefined; // alarms are only handled from main site
    newReport: ISubscription | undefined; // subscribed for the logged-in user
    // ocpp data of subscribed sites; key is siteId or charger_connector for rfids
    chargerData: { [key: number | string]: ISubscription | undefined };
    // charger status notifications of subscribed sites; key is siteId or charger_connector for rfids
    chargerStatusNotification: { [key: number | string]: ISubscription | undefined };
    // ocpp charging session data; key is siteId or user's rfid
    chargingSessionData: { [key: number | string]: ISubscription | undefined };
  } = {
    liveData: undefined,
    newPeriod: undefined,
    newAlarm: undefined,
    newReport: undefined,
    chargerData: {},
    chargerStatusNotification: {},
    chargingSessionData: {},
  };

  constructor(api: V6Client<never>, store: Store) {
    this.API = api;
    this.store = store;
  }

  protected getUsedSites(
    selectedSite: number | undefined,
    virtualSite: IVirtSiteAssign | undefined,
  ) {
    const siteIds: number[] = [];
    // add main site
    if (isSet(selectedSite)) {
      siteIds.push(selectedSite);
    }
    // create list of sites we are using
    Object.values(virtualSite ?? {}).forEach((curGroup) => {
      (curGroup as IVirtAssign[]).forEach((curItem) => {
        if (!siteIds.includes(curItem.sourceSite)) {
          siteIds.push(curItem.sourceSite);
        }
      });
    });
    return siteIds;
  }

  public *setupForPushData(
    clientToken: string,
    siteId: number | undefined,
    userId: number | undefined,
    rfid: string | undefined,
    pendingChargingSession: IOcppChargingSession[],
    selection: IRegisterPushSelection,
    forceUpdate: boolean,
    useProxy: boolean, // controller live data is pulled via the proxy, not pushed
  ) {
    // this gets called repeatedly!
    // store client token for potential unsubscribe action
    this.clientToken = clientToken;
    // whether the controller is reachable through the proxy (reported by the cloud)
    let proxyAvailable = false;

    try {
      // reports belong to the user, so they are subscribed independently of the site
      if (selection.needsReportData && isSet(userId)) {
        this.subscribeReportData(userId);
      }

      // register for site or as RFID user?
      if (siteId) {
        if (selection.needsLiveData || selection.needsChargerData) {
          this.registeredSiteId = siteId;
          proxyAvailable = (yield this.registerLiveData(
            siteId,
            undefined,
            true,
            forceUpdate,
            selection.needsControllerChargerData,
            useProxy,
          )) as boolean;
        }

        // subscribe to other data; do this only once for main site
        if (selection.needsLiveData) {
          yield this.subscribeLiveData(siteId, forceUpdate);
        }
        if (selection.needsChargerData) {
          yield this.subscribeChargerData({ siteId }, siteId);
          yield this.subscribeChargerStatusNotification({ siteId }, siteId);
          yield this.subscribeChargingSessionData(siteId, undefined);
        }
        if (selection.needsAlarmData) {
          this.subscribeAlarmData(siteId);
        }
      } else {
        // register for RFID user
        // subscribe all ongoing charging sessions
        for (let i = 0; i < pendingChargingSession.length; i++) {
          const curItem = pendingChargingSession[i];
          yield this.subscribeChargerDataForConnector(curItem.fk_charger, curItem.connector, true);
          yield this.subscribeChargerStatusNotificationForConnector(
            curItem.fk_charger,
            curItem.connector,
            true,
          );
        }
        // also remove subscriptions to all obsolete sessions? (not implemented yet)
        // subscribe to info on new charging sessions
        yield this.subscribeChargingSessionData(undefined, rfid);
        // update rfid user table to let chargers know we need live data
        yield this.registerLiveData(undefined, rfid, true, forceUpdate, false, false);
      }

      // clear potential pending network error
      yield put(raiseAction(ActionType.ACT_REGISTER_FOR_PUSH_DATA_SUCC, proxyAvailable));
    } catch (error) {
      // typically this happens if the network connection got down
      // no do not need to do something else; we will retry on next register tick
      console.warn("cannot register for live data:", (error as any)?.errors?.[0]?.message);
      yield put(raiseAction(ActionType.ACT_REGISTER_FOR_PUSH_DATA_FAIL, error));
    }
    return proxyAvailable;
  }

  private *registerLiveData(
    siteId: number | undefined,
    rfid: string | undefined,
    doRegister: boolean,
    forceUpdate: boolean,
    subControllerChargerData: boolean,
    useProxy: boolean, // when true, controller live data is pulled via the proxy instead
  ) {
    // when the proxy is used, we do not need the costly controller live-data push
    const subControllerPowerData = isSet(siteId) && !useProxy;
    // update site and rfid user table to let chargers know we need live data
    const args: RegisterDataListenerInput = {
      siteId,
      rfid,
      clientId: this.clientToken!,
      doRegister,
      forceUpdate,
      subChargerPowerData: true,
      subChargerStatusData: true,
      subControllerPowerData,
      subControllerChargerData: subControllerChargerData && !useProxy,
      subControllerChargerPing: false,
    };
    const result = (yield this.API?.graphql({
      query: registerDataListenerGql,
      variables: { data: args },
    })) as GraphQLResult<RegisterDataListenerMutation>;
    // the mutation returns a non-zero value if the controller is reachable
    // via the proxy (see cloud-api registerDataListener resolver)
    return Boolean(result?.data?.registerDataListener);
  }

  protected *subscribeLiveData(siteId: number, forceUpdate: boolean) {
    // do we already have a subscription?
    if (!this.graphQlSubscription.liveData) {
      const observable = this.API?.graphql({
        query: onUpdateLiveDataGql,
        variables: {
          siteId,
        } as OnUpdateLiveDataSubscriptionVariables,
      }) as any | undefined;
      this.graphQlSubscription.liveData = observable?.subscribe({
        next: (eventData: { data: OnUpdateLiveDataSubscription; errors?: Error[] }) => {
          if (eventData.errors) {
            console.error("errors in new live data", eventData);
          } else {
            this.gotCloudLiveData(eventData.data);
          }
        },
        error: (error: Error) => {
          // typically this is a "Connection closed" event after the 2 hour timeout at AWS
          // but it may also be a MaxSubscriptionsReachedError error, which is permanent
          console.warn("error in graphQL liveData subscription", error, siteId);
          // reset subscription indicate to reconnect at next register event
          this.graphQlSubscription.liveData = undefined;
        },
      });
    }

    // do the same for period data
    if (!this.graphQlSubscription.newPeriod) {
      const observable = this.API?.graphql({
        query: onNewTrendDataGql,
        variables: {
          siteId,
        } as OnNewTrendDataSubscriptionVariables,
      }) as any | undefined;
      this.graphQlSubscription.newPeriod = observable?.subscribe({
        next: (eventData: { data: OnNewTrendDataSubscription; errors?: Error[] }) => {
          if (eventData.errors) {
            console.error("errors in new period data", eventData);
          } else {
            this.gotCloudPeriodData(eventData.data);
          }
        },
        error: (error: Error) => {
          // typically this is a "Connection closed" event after the 2 hour timeout at AWS
          // but it may also be a MaxSubscriptionsReachedError error, which is permanent
          console.warn("error in graphQL newPeriod subscription", error, siteId);
          // reset subscription indicate to reconnect at next register event
          this.graphQlSubscription.newPeriod = undefined;
        },
      });
    }
  }

  protected subscribeAlarmData(siteId: number) {
    // subscribe to alarm data; do this only once for main site
    if (this.graphQlSubscription.newAlarm === undefined) {
      const observable = this.API?.graphql({
        query: onNewAlarmDataGql,
        variables: {
          siteId,
        } as OnNewAlarmDataSubscriptionVariables,
      }) as any | undefined;
      this.graphQlSubscription.newAlarm = observable?.subscribe({
        next: (eventData: { data: OnNewAlarmDataSubscription; errors?: Error[] }) => {
          if (eventData.errors) {
            console.error("errors in new alarm data", eventData);
          } else {
            this.gotCloudAlarmData(eventData.data);
          }
        },
        error: (error: Error) => {
          // typically this is a "Connection closed" event after the 2 hour timeout at AWS
          // but it may also be a MaxSubscriptionsReachedError error, which is permanent
          console.warn("error in graphQL alarm subscription", error);
          // reset subscription indicate to reconnect at next register event
          this.graphQlSubscription.newAlarm = undefined;
        },
      });
    }
  }

  protected subscribeReportData(userId: number) {
    if (!this.graphQlSubscription.newReport) {
      const observable = this.API?.graphql({
        query: onNewReportGql,
        variables: {
          userId,
        } as OnNewReportSubscriptionVariables,
      }) as any | undefined;
      this.graphQlSubscription.newReport = observable?.subscribe({
        next: (eventData: { data: OnNewReportSubscription; errors?: Error[] }) => {
          if (eventData.errors) {
            console.error("errors in new report", eventData);
          } else {
            this.gotCloudReport(eventData.data);
          }
        },
        error: (error: Error) => {
          // typically this is a "Connection closed" event after the 2 hour timeout at AWS
          // but it may also be a MaxSubscriptionsReachedError error, which is permanent
          console.warn("error in graphQL newReport subscription", error, userId);
          // reset subscription indicate to reconnect at next register event
          this.graphQlSubscription.newReport = undefined;
        },
      });
    }
  }

  private subscribeChargerData(
    args: OnNewChargerDataSubscriptionVariables,
    subscriptionKey: string | number,
  ) {
    // already subscribed?
    if (this.graphQlSubscription.chargerData[subscriptionKey] === undefined) {
      //console.log("subscribing ocpp data", args);
      const observable = this.API?.graphql({
        query: onNewChargerDataGql,
        variables: args,
      }) as any | undefined;
      this.graphQlSubscription.chargerData[subscriptionKey] = observable?.subscribe({
        next: (eventData: { data: OnNewChargerDataSubscription; errors?: Error[] }) => {
          if (eventData.errors) {
            console.error("errors in new charger data", eventData);
          } else {
            this.gotChargerData(eventData.data);
          }
        },
        error: (error: Error) => {
          // typically this is a "Connection closed" event after the 2 hour timeout at AWS
          // but it may also be a MaxSubscriptionsReachedError error, which is permanent
          console.warn("error in graphQL charger subscription", error);
          // reset subscription indicate to reconnect at next register event
          this.graphQlSubscription.chargerData[subscriptionKey] = undefined;
        },
      });
    }
  }

  public *subscribeChargerDataForConnector(
    chargerId: number,
    connector: number,
    doRegister: boolean,
  ) {
    const subscriptionKey = `${chargerId}_${connector}`;
    // subscribe or unsubscribe?
    if (doRegister) {
      yield this.subscribeChargerData({ chargerId, connector }, subscriptionKey);
    } else {
      // unsubscribe from session
      console.warn("unsubscribing ocpp data", subscriptionKey);
      yield this.graphQlSubscription.chargerData[subscriptionKey]?.unsubscribe();
      delete this.graphQlSubscription.chargerData[subscriptionKey];
    }
  }

  private subscribeChargerStatusNotification(
    args: OnNewChargerStatusNotificationSubscriptionVariables,
    subscriptionKey: string | number,
  ) {
    // already subscribed?
    if (this.graphQlSubscription.chargerStatusNotification[subscriptionKey] === undefined) {
      //console.log("subscribing ocpp data", args);
      const observable = this.API?.graphql({
        query: onNewChargerStatusNotification,
        variables: args,
      }) as any | undefined;
      this.graphQlSubscription.chargerStatusNotification[subscriptionKey] = observable?.subscribe({
        next: (eventData: {
          data: OnNewChargerStatusNotificationSubscription;
          errors?: Error[];
        }) => {
          if (eventData.errors) {
            console.error("errors in chargers status notification", eventData);
          } else {
            this.gotChargerStatusNotification(eventData.data);
          }
        },
        error: (error: Error) => {
          // typically this is a "Connection closed" event after the 2 hour timeout at AWS
          // but it may also be a MaxSubscriptionsReachedError error, which is permanent
          console.warn("error in graphQL status notification subscription", error);
          // reset subscription indicate to reconnect at next register event
          this.graphQlSubscription.chargerStatusNotification[subscriptionKey] = undefined;
        },
      });
    }
  }

  public *subscribeChargerStatusNotificationForConnector(
    chargerId: number,
    connector: number,
    doRegister: boolean,
  ) {
    const subscriptionKey = `${chargerId}_${connector}`;
    // subscribe or unsubscribe?
    if (doRegister) {
      yield this.subscribeChargerStatusNotification({ chargerId, connector }, subscriptionKey);
    } else {
      // unsubscribe from session
      console.warn("unsubscribing charger status notification", subscriptionKey);
      yield this.graphQlSubscription.chargerStatusNotification[subscriptionKey]?.unsubscribe();
      delete this.graphQlSubscription.chargerStatusNotification[subscriptionKey];
    }
  }

  protected subscribeChargingSessionData(siteId: number | undefined, rfid: string | undefined) {
    const subscriptionKey = siteId ?? rfid ?? 0;
    // subscribe to charging session data; do this only once for main site
    if (this.graphQlSubscription.chargingSessionData[subscriptionKey] === undefined) {
      const observable = this.API?.graphql({
        query: onNewChargingSessionGql,
        variables: {
          siteId,
          rfid,
        } as OnNewChargingSessionDataSubscriptionVariables,
      }) as any | undefined;
      this.graphQlSubscription.chargingSessionData[subscriptionKey] = observable?.subscribe({
        next: (eventData: { data: OnNewChargingSessionDataSubscription; errors?: Error[] }) => {
          if (eventData.errors) {
            console.error("errors in new charging session data", eventData);
          } else {
            this.gotChargingSessionData(eventData.data);
          }
        },
        error: (error: Error) => {
          // typically this is a "Connection closed" event after the 2 hour timeout at AWS
          // but it may also be a MaxSubscriptionsReachedError error, which is permanent
          console.warn("error in graphQL charging session subscription", error);
          // reset subscription indicate to reconnect at next register event
          this.graphQlSubscription.chargingSessionData[subscriptionKey] = undefined;
        },
      });
    }
  }

  protected gotCloudLiveData(message: OnUpdateLiveDataSubscription) {
    const data = message.onUpdateLiveData!;
    const evtData: ILiveDataCloud = {
      siteId: data.siteId,
      systemTime: data.timestamp,
      curTimeOfPeriod: data.curTimeOfPeriod ?? undefined,
      accEnergy: data.accEnergy ?? undefined,
      curEnergy: data.curEnergy ?? undefined,
      curPower: data.curPower ?? undefined,
      curTargetValues: data.targetValues ?? undefined,
      curRegulationPriorityStatus: data.regulationPriorityStatus ?? undefined,
      curTrendLevel: data.curTrendLevel ?? undefined,
      energyMeterValues: data.curEnergyMeterValues ?? undefined,
      inputStatus: data.curInputStatus ?? undefined,
      outputStatus: data.curOutputStatus ?? undefined,
      outputStatusPhysical: data.curOutputStatusPhysical ?? undefined,
      conditionStatus: data.curConditionStatus ?? undefined,
      moduleStatus: data.curModuleStatus ?? undefined,
      chargerCurP: data.chargerCurP ?? undefined,
      chargerCurI: data.chargerCurI ?? undefined,
      chargerCurU: data.chargerCurU ?? undefined,
      chargerDevMaxI: data.chargerDevMaxI ?? undefined,
      chargerSetMaxI: data.chargerSetMaxI ?? undefined,
      chargerETotal: data.chargerETotal ?? undefined,
      chargerESession: data.chargerESession ?? undefined,
      chargerState: data.chargerState ?? undefined,
      chargerAuth: data.chargerAuth ?? undefined,
      pingResult: data.pingResult ?? undefined,
    };
    this.store?.dispatch(raiseAction(ActionType.ACT_GET_CLOUDLIVEDATA_SUCC, evtData));

    // also check for command execution feedback
    const state = this.store?.getState() as IState | undefined;
    const pendingCommand = state?.scheduler.pendingCommand;
    // do we have a command pending?
    if (pendingCommand) {
      let isFailed: boolean | undefined;
      let payload;

      // check for confirmation
      if (data.commandSeqNr === pendingCommand.seqNr) {
        // any result is treated as an error message
        isFailed = isSet(data.commandResult);
        payload = isFailed ? data.commandResult : pendingCommand.cmdArgs;
      } else {
        // check for timeout failure
        if (Date.now() - pendingCommand.timestampMs > this.CLOUD_COMMAND_TIMEOUT) {
          isFailed = true;
          payload = "command execution timeout";
        }
      }

      // do we provide a result?
      if (isSet(isFailed)) {
        const action =
          pendingCommand.command === Command.setInput ?
            isFailed ? ActionType.ACT_CHANGE_INPUT_FAIL
            : ActionType.ACT_CHANGE_INPUT_SUCC
          : pendingCommand.command === Command.setOutput ?
            isFailed ? ActionType.ACT_CHANGE_OUTPUT_FAIL
            : ActionType.ACT_CHANGE_OUTPUT_SUCC
          : pendingCommand.command === Command.openSshTunnel ?
            isFailed ? ActionType.ACT_OPEN_SSH_TUNNEL_FAIL
            : ActionType.ACT_OPEN_SSH_TUNNEL_SUCC
          : pendingCommand.command === Command.rebootController ?
            isFailed ? ActionType.ACT_REBOOT_FAIL
            : ActionType.ACT_REBOOT_SUCC
          : undefined;
        if (action) {
          this.store?.dispatch(raiseAction(action, payload));
        } else {
          console.error("unexpected command", pendingCommand.command);
        }
      }
    }
  }

  protected gotCloudPeriodData(message: OnNewTrendDataSubscription) {
    const curData = message.onNewTrendData;
    if (curData) {
      const trendRow = cloudTrend2LinxTrend(curData);
      this.store?.dispatch(raiseAction(ActionType.ACT_APPEND_CLOUDTREND, trendRow));
    } else {
      console.warn("got empty period data", message);
    }
  }

  protected gotCloudReport(message: OnNewReportSubscription) {
    const curData = message.onNewReport;
    if (curData && curData.outputFormat === ReportFormat.link) {
      const payload: IActionNewReport = {
        siteId: curData.siteId ?? undefined,
        reportId: curData.reportId,
        link: curData.result,
        error: curData.error ?? undefined,
      };
      this.store?.dispatch(raiseAction(ActionType.ACT_NEW_REPORT, payload));
    } else if (curData && curData.outputFormat === ReportFormat.data) {
      let jsonData;
      try {
        jsonData = JSON.parse(curData.result);
      } catch (error) {
        console.error("error parsing JSON data for report-data");
      }
      if (isSet(jsonData)) {
        const payload: IActionNewReportData = {
          siteId: curData.siteId ?? undefined,
          reportId: curData.reportId,
          data: jsonData,
          error: undefined,
        };
        this.store?.dispatch(raiseAction(ActionType.ACT_NEW_REPORT_DATA, payload));
      } else {
        this.store?.dispatch(raiseAction(ActionType.ACT_CREATE_REPORT_FAIL, curData.error));
      }
    } else {
      console.warn("got empty report", message);
      // we should set the internal state / report to valid values
      // otherwise "generating report is shown all the time"
      // and only on the console we know about the issue!
      // shouldn' there get an Error raised???
      const payload: IActionNewReport = {
        siteId: curData?.siteId ?? undefined,
        reportId: curData?.reportId ?? undefined,
        link: curData?.result ?? "",
        error: "internal error while creating report",
      };
      this.store?.dispatch(raiseAction(ActionType.ACT_CREATE_REPORT_FAIL, payload.error));
    }
  }

  protected gotCloudAlarmData(message: OnNewAlarmDataSubscription) {
    const curData = message.onNewAlarmData;
    if (curData) {
      const alarmRow: IAlarmEntry = {
        source: curData.source as AlarmSource,
        alarmTimestamp: curData.alarmTimestamp,
        clearTimestamp: curData.clearTimestamp ?? undefined,
        value: curData.value,
        state: getAlarmState(curData.isActive ? true : false, curData.ackTimestamp ? true : false),
        description: curData.description ?? undefined,
      };
      this.store?.dispatch(raiseAction(ActionType.ACT_APPEND_CLOUDALARM, alarmRow));
      // if we got an alarm ACK, we need to reload the alarms
      // as an ACK also ACKs older alarms of same type
      if (isSet(curData.ackTimestamp)) {
        this.store?.dispatch(raiseAction(ActionType.ACT_GET_ALARMS_REQ));
      }
    }
  }

  protected gotChargerData(message: OnNewChargerDataSubscription) {
    const curData = message.onNewChargerData;
    const payload: IActionNewChargerData = {
      chargerId: curData.chargerId,
      timestamp: curData.timestamp,
      connector: curData.connector ?? 0,
      power: curData.power ?? undefined,
      current: (curData.current as number[] | undefined) ?? undefined,
      voltage: (curData.voltage as number[] | undefined) ?? undefined,
      soc: curData.soc ?? undefined,
      sessionEnergy: curData.sessionEnergy ?? undefined,
      energyMeter: curData.energyMeter ?? undefined,
      commandSeqNr: curData.chargerCmdSeqNr ?? undefined,
      commandHasError: curData.chargerCmdHasError ?? undefined,
      commandResult: curData.chargerCmdResult ?? undefined,
      pingResult: curData.pingResult ?? undefined,
    };
    this.store?.dispatch(raiseAction(ActionType.ACT_NEW_OCPP_DATA, payload));
  }

  protected gotChargerStatusNotification(message: OnNewChargerStatusNotificationSubscription) {
    const curData = message.onNewChargerStatusNotification;
    const payload: IActionNewChargerStatusNotification = {
      chargerId: curData.chargerId,
      timestamp: curData.timestamp,
      connector: curData.connector ?? 0,
      source: curData.source!,
      statusCode: (curData.statusCode as ConnectorStatus | null) ?? undefined,
      errorCode: curData.errorCode ?? undefined,
      info: curData.info ?? undefined,
    };
    this.store?.dispatch(raiseAction(ActionType.ACT_NEW_CHARGER_STATUS, payload));
  }

  protected gotChargingSessionData(message: OnNewChargingSessionDataSubscription) {
    const curData = message.onNewChargingSessionData;
    const payload: IActionNewChargingSessionData = {
      id: parseApiInt(curData.id),
      chargerId: curData.chargerId,
      connector: curData.connector ?? 0,
      rfid: curData.rfid,
      rfidId: undefined,
      startDate: curData.startDate ?? undefined,
      stopDate: curData.stopDate ?? undefined,
      startMeter: curData.startMeter ?? undefined,
      stopMeter: curData.stopMeter ?? undefined,
      stopReason: (curData.stopReason as StopReason) ?? undefined,
      tariffId: curData.tariffId ?? undefined,
      costs: curData.costs ?? undefined,
    };
    // publish event
    this.store?.dispatch(raiseAction(ActionType.ACT_NEW_CHARGING_SESSION_DATA, payload));
    // if we just started a new session, we also need to reload the session list
    // as we cannot just add the session due to filtering
    if (!curData.stopDate) {
      this.store?.dispatch(raiseAction(ActionType.ACT_GET_OCPP_SESSIONS_REQ));
    }
  }

  // the site is tracked here, as the selected site is already reset on disconnect
  public *unregisterPushData() {
    const siteId = this.registeredSiteId;
    if (!isSet(siteId)) {
      return;
    }
    this.registeredSiteId = undefined;
    try {
      // let LINX device know that we do not need the live data anymore
      yield this.registerLiveData(siteId, undefined, false, false, false, false);
    } catch (error) {
      // this can happen if the network connection got down
      console.error("cannot deregister push data:", error);
    }
  }

  public *unsubscribePushData() {
    try {
      // unsubscribe from live data of a potential existing connection
      if (isSet(this.graphQlSubscription.liveData)) {
        yield this.graphQlSubscription.liveData.unsubscribe();
        this.graphQlSubscription.liveData = undefined;
      }
      if (isSet(this.graphQlSubscription.newPeriod)) {
        yield this.graphQlSubscription.newPeriod.unsubscribe();
        this.graphQlSubscription.newPeriod = undefined;
      }
      // same for reports
      if (isSet(this.graphQlSubscription.newReport)) {
        yield this.graphQlSubscription.newReport.unsubscribe();
        this.graphQlSubscription.newReport = undefined;
      }
      // also for alarm
      if (isSet(this.graphQlSubscription.newAlarm)) {
        yield this.graphQlSubscription.newAlarm.unsubscribe();
        this.graphQlSubscription.newAlarm = undefined;
      }

      // subscription of session info
      let sessionKeys = Object.keys(this.graphQlSubscription.chargingSessionData);
      for (let i = 0; i < sessionKeys.length; i++) {
        const curKey = sessionKeys[i] as any as number | string; // we know it better...
        yield this.graphQlSubscription.chargingSessionData[curKey]?.unsubscribe();
        delete this.graphQlSubscription.chargingSessionData[curKey];
      }
      // subscription of connector data of pending sessions for RFID users
      // or sites for enline users
      sessionKeys = Object.keys(this.graphQlSubscription.chargerData);
      for (let i = 0; i < sessionKeys.length; i++) {
        const curKey = sessionKeys[i] as any as number | string; // we know it better...
        yield this.graphQlSubscription.chargerData[curKey]?.unsubscribe();
        delete this.graphQlSubscription.chargerData[curKey];
      }
    } catch (error) {
      // this can happen if the network connection got down
      console.error("cannot unsubscribe pull data:", error);
      // we need to reset our subscriptions here to be able to resubscribe later
      this.graphQlSubscription = {
        liveData: undefined,
        newPeriod: undefined,
        newAlarm: undefined,
        newReport: undefined,
        chargerData: {},
        chargerStatusNotification: {},
        chargingSessionData: {},
      };
    }
  }
}
